import React from 'react';
import { FiBookOpen, FiUsers, FiCheckCircle, FiClock, FiShield,  FiBarChart2, FiMail, FiCalendar,  FiTrendingUp } from 'react-icons/fi';

const DegreeCollegeGuide = () => {
  const features = [
    {
      icon: <FiBookOpen className="w-6 h-6" />,
      title: 'Course and Program Management',
      description: 'Degree colleges offer multiple programs across various disciplines. The system must handle UG programs, PG programs, diploma courses, and professional certifications. Each program has unique course structures, credit requirements, and graduation criteria.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiCalendar className="w-6 h-6" />,
      title: 'Semester System Management',
      description: 'Most degree colleges follow semester systems. The ERP must manage semester-wise course offerings, exam schedules, grade processing, and student progression. Semester transitions should be seamless, with all records updating automatically.',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Student Enrollment and Registration',
      description: 'Managing student enrollment across semesters is complex. Students choose electives, register for courses, and track their progress toward graduation. The system should support online registration, waitlisting, and course change requests.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'Credit and Grade Point Management',
      description: 'Degree colleges use credit systems and grade point averages. The ERP must calculate SGPA, CGPA, and cumulative grades automatically. Credit tracking ensures students meet graduation requirements without manual calculation.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Faculty Management and Workload',
      description: 'Faculty in degree colleges have complex workload patterns—teaching, research, administrative duties, and student mentoring. The system should track faculty workload, allocate courses, and ensure fair distribution.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiMail className="w-6 h-6" />,
      title: 'Student Services and Support',
      description: 'Degree colleges need comprehensive student support systems—counseling services, career guidance, placement support, and extracurricular tracking. The ERP should be the central hub for all student services.',
      color: 'bg-blue-50'
    }
  ];

  const benefits = [
    {
      title: 'Automated Grade Calculation',
      description: 'Calculate SGPA, CGPA, and cumulative grades automatically. No more manual calculations or spreadsheet errors.',
      icon: <FiBarChart2 className="w-5 h-5" />
    },
    {
      title: 'Semester Transition',
      description: 'Students move from one semester to the next seamlessly. All records, grades, and enrollment data update automatically.',
      icon: <FiCalendar className="w-5 h-5" />
    },
    {
      title: 'Real-Time Student Progress',
      description: 'Track student progress toward graduation in real-time. Identify at-risk students early and provide timely interventions.',
      icon: <FiTrendingUp className="w-5 h-5" />
    },
    {
      title: 'Comprehensive Reporting',
      description: 'Generate reports for accreditation, management, and compliance. Everything you need, instantly available.',
      icon: <FiShield className="w-5 h-5" />
    }
  ];

  const challenges = [
    {
      title: 'Managing Multiple Programs',
      description: 'UG programs, PG programs, diploma courses, and certifications—all with different requirements. The system must handle the complexity of multiple program types.'
    },
    {
      title: 'Credit System Complexity',
      description: 'Different programs have different credit structures. The ERP must handle varied credit requirements without forcing a one-size-fits-all approach.'
    },
    {
      title: 'Faculty Allocation Across Programs',
      description: 'Faculty teach across multiple programs. The system should manage faculty allocation efficiently, avoiding conflicts and ensuring fair distribution.'
    },
    {
      title: 'Student Progression Tracking',
      description: 'Students must meet specific requirements to progress. The ERP should automatically identify students who are at risk and alert advisors.'
    },
    {
      title: 'Accreditation and Compliance',
      description: 'Degree colleges must meet accreditation standards. The system should generate reports that satisfy NAAC, NIRF, and other accreditation requirements.'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Degree colleges are where students become professionals. Whether it's a three-year undergraduate program or a two-year postgraduate degree, these institutions shape careers, build expertise, and prepare students for the workforce or further academic pursuits.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Managing a degree college is complex. <strong className="text-slate-900">Multiple programs. Credit-based systems. Semester transitions. Faculty allocation. Accreditation requirements.</strong> Each aspect requires careful management, and errors can have significant consequences—for students, for accreditation, and for the institution's reputation.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        An effective degree college ERP transforms this complexity into streamlined efficiency. Here's what you need to know.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">40,000+</div>
          <div className="text-sm text-slate-500">Degree colleges across India</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">2 Crore+</div>
          <div className="text-sm text-slate-500">Students enrolled in degree programs</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-rose-600">60%</div>
          <div className="text-sm text-slate-500">Time saved with automated grade processing</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Essential Features for Degree College ERP</h2>

      {/* Features List */}
      {features.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm">{item.description}</p>
            </div>
          </div>
        </div>
      ))}

      {/* Benefits Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">What a Good Degree College ERP Delivers</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {benefits.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200 flex items-start gap-3">
              <div className="bg-white rounded-lg p-2 flex-shrink-0">
                {item.icon}
              </div>
              <div>
                <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                <p className="text-xs text-slate-600">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Challenges Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Complexities in Degree College Management</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {challenges.map((item, i) => (
            <div key={i} className="bg-amber-50 rounded-xl p-4 border border-amber-200">
              <div className="flex items-start gap-2">
                <span className="text-amber-500 font-bold text-sm">⚠️</span>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">PU College vs. Degree College ERP</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
            <div className="text-sm font-semibold text-blue-700 mb-2">PU College ERP</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Stream-based education (Science, Commerce, Arts)</span></li>
              <li className="flex items-start gap-2">• <span>Subject combinations and electives</span></li>
              <li className="flex items-start gap-2">• <span>Board exam preparation and tracking</span></li>
              <li className="flex items-start gap-2">• <span>Competitive exam preparation integration</span></li>
              <li className="flex items-start gap-2">• <span>Parent communication for teenagers</span></li>
            </ul>
          </div>
          <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
            <div className="text-sm font-semibold text-purple-700 mb-2">Degree College ERP</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Program-based education (UG and PG)</span></li>
              <li className="flex items-start gap-2">• <span>Credit and semester systems</span></li>
              <li className="flex items-start gap-2">• <span>SGPA/CGPA calculations</span></li>
              <li className="flex items-start gap-2">• <span>Faculty workload and research management</span></li>
              <li className="flex items-start gap-2">• <span>Accreditation and compliance reporting</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon is the Best Choice for Degree Colleges</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📚</div>
            <h4 className="font-bold text-slate-900 text-sm">Program Management</h4>
            <p className="text-xs text-slate-600">Handle UG, PG, diploma, and certification programs</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Credit System</h4>
            <p className="text-xs text-slate-600">Automatic SGPA/CGPA calculations and credit tracking</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🎓</div>
            <h4 className="font-bold text-slate-900 text-sm">Semester Management</h4>
            <p className="text-xs text-slate-600">Seamless semester transitions and enrollment</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📋</div>
            <h4 className="font-bold text-slate-900 text-sm">Accreditation Support</h4>
            <p className="text-xs text-slate-600">NAAC and NIRF ready reports</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">👨‍🏫</div>
            <h4 className="font-bold text-slate-900 text-sm">Faculty Management</h4>
            <p className="text-xs text-slate-600">Workload tracking and course allocation</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Real-Time Analytics</h4>
            <p className="text-xs text-slate-600">Student progress and institutional insights</p>
          </div>
        </div>
      </div>

      {/* Decision Checklist */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">Degree College ERP Checklist</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex items-start gap-2 bg-green-50 rounded-lg p-3 border border-green-200">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Does it handle multiple programs (UG, PG, Diploma)?</span>
          </div>
          <div className="flex items-start gap-2 bg-green-50 rounded-lg p-3 border border-green-200">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Can it manage credit systems and SGPA/CGPA?</span>
          </div>
          <div className="flex items-start gap-2 bg-green-50 rounded-lg p-3 border border-green-200">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Does it support semester-based academic management?</span>
          </div>
          <div className="flex items-start gap-2 bg-green-50 rounded-lg p-3 border border-green-200">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Can it generate reports for NAAC, NIRF, and other accreditations?</span>
          </div>
          <div className="flex items-start gap-2 bg-green-50 rounded-lg p-3 border border-green-200">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Does it track faculty workload across programs?</span>
          </div>
          <div className="flex items-start gap-2 bg-green-50 rounded-lg p-3 border border-green-200">
            <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-slate-700">Does it provide real-time student progress tracking?</span>
          </div>
        </div>
      </div>

      {/* The Student Perspective */}
      <div className="mt-8 bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
        <h3 className="text-lg font-bold text-emerald-900 mb-3">Why This Matters for Students</h3>
        <p className="text-emerald-800 leading-relaxed mb-3">
          Behind every system are students whose futures depend on their college experience. When a degree college ERP works well, students benefit in tangible ways.
        </p>
        <ul className="text-emerald-800 leading-relaxed space-y-2">
          <li className="flex items-start gap-2">• <span>Clear visibility into their academic progress and graduation requirements</span></li>
          <li className="flex items-start gap-2">• <span>Timely access to grades and academic records</span></li>
          <li className="flex items-start gap-2">• <span>Early identification of academic challenges through real-time tracking</span></li>
          <li className="flex items-start gap-2">• <span>Seamless registration for courses each semester</span></li>
          <li className="flex items-start gap-2">• <span>Better support through integrated student services</span></li>
        </ul>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Degree Colleges Need Specialized ERPs</h3>
        <p className="text-indigo-800 leading-relaxed">
          Degree colleges are complex institutions managing multiple programs, credit systems, faculty workloads, and accreditation requirements. General purpose school or college software simply cannot handle this complexity.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          An ERP designed specifically for degree colleges transforms administrative complexity into streamlined efficiency. Automated grade calculations. Seamless semester transitions. Real-time student progress tracking. Compliance-ready reporting.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon is built for degree colleges.</strong> Program management. Credit systems. Faculty management. Accreditation support. All in one platform. Ready to transform your degree college.
        </p>
      </div>
    </>
  );
};

export default DegreeCollegeGuide;